import { useCallback, useEffect, useRef } from "react";
import { useWidgetStore } from "../store";
import {
  VoiceWidgetConfig,
  WidgetConfig,
  ThemeConfig,
  EmbedConfig,
  Message,
  Suggestion,
  ConversationItem,
} from "../store/types";
import { defaultTheme, getThemeValue } from "./theme";

// Re-export types for backward compatibility
export type {
  VoiceWidgetConfig,
  WidgetConfig,
  ThemeConfig,
  EmbedConfig,
  Message,
  Suggestion,
  ConversationItem,
};

// Re-export theme utilities for backward compatibility
export { defaultTheme, getThemeValue };

import { useWidgetConfiguration } from './useWidgetConfiguration';
import { useQuotaManagement } from './useQuotaManagement';
import { useSessionLifecycle } from './useSessionLifecycle';
import { useConversationConnection } from './useConversationConnection';
import { useMessageHandling } from './useMessageHandling';
import { useSuggestionSystem } from './useSuggestionSystem';
import { useClientTools, GPS_WARMUP_CAP_MS } from './useClientTools';
import { useWidgetCoordination } from './useWidgetCoordination';
import { useHeartbeat } from './useHeartbeat';
import { buildGpsDynamicVariables, useGeolocation } from './useGeolocation';
import {
  selectButtonState,
  selectConnectionStatus,
  selectConversationItems,
  selectError,
  selectHasQuota,
  selectIsConnected,
  selectIsRecording,
  selectIsAgentSpeaking,
  selectIsAgentThinking,
  selectIsWidgetDisabled,
  selectMessages,
  selectQuotaCheckComplete,
  selectTranscript,
  selectWelcomeMessage,
  selectWidgetName,
} from "../store/selectors";
import { buildSignedUrlEndpoint, getCurrentDomain, getCurrentPath } from '../utils/apiUtils';
import {
  getConversationTransportOptions,
  preferredTransportOrder,
  TransportKind,
} from '../utils/conversationTransport';
import { checkVoiceCompatibility } from '../utils/compatibility';
import { compatibilityError, translateRuntimeError, UserFacingError } from '../utils/errorMessages';
import { buildConversationClientTools } from '../utils/conversationClientTools';
import { warmupGpsBeforeSession } from '../utils/gpsSession';
import { switchTextSessionToVoice } from '../utils/voiceHandoff';
import { endConversationSession } from '../services/api';
import {
  BackendSessionCleanupError,
  ConversationStartCancelledError,
  startIssuedConversation,
} from '../utils/conversationStartup';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function storeUserError(uf: UserFacingError): void {
  useWidgetStore.getState().setError(uf);
}

// Signed-url may refresh display name/welcome after session start.
// Mode / uiVariant / gps shell authority is resolved at mount via
// GET /api/widget/config (see useWidgetConfiguration) — not here.
function applySignedUrlWidgetConfig(payload: unknown): void {
  if (!isRecord(payload) || !isRecord(payload.widgetConfig)) return;
  const wc = payload.widgetConfig;
  const store = useWidgetStore.getState();
  if (typeof wc.widgetName === 'string' && wc.widgetName.length > 0) {
    store.setWidgetName(wc.widgetName);
  }
  if (typeof wc.welcomeMessage === 'string' && wc.welcomeMessage.length > 0) {
    store.setWelcomeMessage(wc.welcomeMessage);
  }
}

export type UseVoiceWidgetOptions = {
  /**
   * When false, skip GET /api/widget/config (admin Live preview).
   * Default true for production embeds.
   */
  loadPublicConfig?: boolean;
};

export const useVoiceWidget = (
  config: WidgetConfig | VoiceWidgetConfig,
  options: UseVoiceWidgetOptions = {}
) => {
  // State from Zustand store
  const buttonState = useWidgetStore(selectButtonState);
  const connectionStatus = useWidgetStore(selectConnectionStatus);
  const error = useWidgetStore(selectError);
  const messages = useWidgetStore(selectMessages);
  const transcript = useWidgetStore(selectTranscript);
  const conversationItems = useWidgetStore(selectConversationItems);
  const hasQuota = useWidgetStore(selectHasQuota);
  const quotaCheckComplete = useWidgetStore(selectQuotaCheckComplete);
  const isWidgetDisabled = useWidgetStore(selectIsWidgetDisabled);
  const widgetName = useWidgetStore(selectWidgetName);
  const welcomeMessage = useWidgetStore(selectWelcomeMessage);
  const isAgentThinking = useWidgetStore(selectIsAgentThinking);

  // 1. Configuration (shell waits for shellDecided when public config is needed)
  const { processedConfig, errors, isValid, shellDecided } =
    useWidgetConfiguration(config, {
      loadPublicConfig: options.loadPublicConfig,
    });

  useEffect(() => {
    const store = useWidgetStore.getState();
    if (
      typeof processedConfig.widgetName === 'string' &&
      processedConfig.widgetName.trim().length > 0
    ) {
      store.setWidgetName(processedConfig.widgetName.trim());
    }
    if (
      typeof processedConfig.welcomeMessage === 'string' &&
      processedConfig.welcomeMessage.trim().length > 0
    ) {
      store.setWelcomeMessage(processedConfig.welcomeMessage.trim());
    }
  }, [processedConfig.widgetName, processedConfig.welcomeMessage]);

  // 2. Quota
  const { checkQuota } = useQuotaManagement(
    processedConfig.apiUrl,
    processedConfig.debug
  );

  // 3. Session
  const { sessionId, createSession, endSession } = useSessionLifecycle(
    processedConfig.apiUrl,
    processedConfig.debug
  );

  const widgetId = processedConfig.widgetId || 'default-widget';

  // Guard against double-click while an async start is in flight — prevents
  // two /api/signed-url calls and two orphan sessions.
  const startingRef = useRef(false);
  const activeSessionModeRef = useRef<'text' | 'voice' | null>(null);

  const handleIdleTimeout = useCallback(async () => {
    await endSession();
    activeSessionModeRef.current = null;
  }, [endSession]);

  const handleRemoteDisconnect = useCallback(async () => {
    await endSession();
    activeSessionModeRef.current = null;
  }, [endSession]);

  // 4. Widget Coordination (is used by useConversationConnection)
  const { isWidgetActive } = useWidgetCoordination(
    widgetId,
  );

  // 5. Conversation
  const {
    conversation,
    conversationService,
    conversationId,
    isConnected,
    startConversation,
    stopConversation,
    markUserActivity,
  } = useConversationConnection(
    processedConfig,
    widgetId,
    isWidgetActive,
    handleIdleTimeout,
    handleRemoteDisconnect
  );

  // 6. Suggestions
  const { handleMarkAsSuggested, addTestSuggestion } = useSuggestionSystem(
    processedConfig.debug
  );

  const gpsEnabled = processedConfig.gpsEnabled === true;

  // 7. Client Tools (including GPS)
  const { handleOpenWidget, handleOpenUrl, handleGetCurrentLocation, warmupGps } = useClientTools(
    processedConfig.debug,
    gpsEnabled
  );

  // 7b. Geolocation (only when gpsEnabled)
  const { latitude, longitude } = useGeolocation(gpsEnabled);
  const dynamicVariables = buildGpsDynamicVariables(gpsEnabled, latitude, longitude);

  // 8. Heartbeat
  useHeartbeat(sessionId, processedConfig.apiUrl, processedConfig.debug);

  const startWidgetConversation = useCallback(
    async (textOnly: boolean, sessionMode: 'text' | 'voice') => {
      useWidgetStore.getState().setButtonState('connecting');
      const widgetId = processedConfig.widgetId;
      if (!widgetId) throw new Error('Widget ID is required to start a conversation');

      await warmupGpsBeforeSession(
        processedConfig.gpsEnabled === true,
        warmupGps,
        GPS_WARMUP_CAP_MS
      );

      const clientTools = buildConversationClientTools(
        processedConfig.gpsEnabled === true,
        {
          mark_as_suggested: handleMarkAsSuggested,
          open_widget: handleOpenWidget,
          open_url: handleOpenUrl,
          open_link: handleOpenUrl,
          open_booking_link: handleOpenUrl,
        },
        handleGetCurrentLocation
      );

      // Firefox: WebRTC token first, then signed WebSocket.
      // Chrome/Safari: WebSocket first, WebRTC fallback.
      const transports = preferredTransportOrder();
      let lastError: unknown;

      for (let i = 0; i < transports.length; i++) {
        const transport: TransportKind = transports[i];
        try {
          const endpoint = buildSignedUrlEndpoint(processedConfig.apiUrl, {
            domain: getCurrentDomain(),
            widgetId,
            path: getCurrentPath(),
            transport,
          });
          const { conversationId: newConversationId } = await startIssuedConversation({
            requestSignedSession: async () => {
              const res = await fetch(endpoint);
              if (!res.ok) throw new Error('Failed to get signed URL');
              const response: unknown = await res.json();
              applySignedUrlWidgetConfig(response);
              return response;
            },
            startSdkConversation: (signedSession) => startConversation({
              ...getConversationTransportOptions(
                {
                  signedUrl: signedSession.signedUrl,
                  conversationToken: signedSession.conversationToken,
                },
                textOnly
              ),
              textOnly,
              overrides: { conversation: { textOnly } },
              clientTools,
              dynamicVariables,
            }),
            acceptBackendSession: createSession,
            stopSdkConversation: () => stopConversation({ preserveConversation: true }),
            endBackendSession: (sessionId) =>
              endConversationSession(processedConfig.apiUrl, sessionId),
          });
          activeSessionModeRef.current = sessionMode;
          if (i > 0) {
            console.info(
              `[Widget] Connected via fallback transport: ${transport}`
            );
          }
          return newConversationId;
        } catch (err) {
          lastError = err;
          if (err instanceof ConversationStartCancelledError) return undefined;
          if (err instanceof BackendSessionCleanupError) break;
          const hasFallback = i < transports.length - 1;
          // Prefer falling through on realtime failures; still retry once for
          // any transport error so Firefox can flip WS ↔ WebRTC.
          if (hasFallback) {
            console.warn(
              `[Widget] Transport ${transport} failed, trying fallback:`,
              err
            );
            continue;
          }
          break;
        }
      }

      storeUserError(translateRuntimeError(lastError));
      useWidgetStore.getState().setButtonState('idle');
      return undefined;
    },
    [
      processedConfig,
      warmupGps,
      handleMarkAsSuggested,
      handleOpenWidget,
      handleOpenUrl,
      handleGetCurrentLocation,
      startConversation,
      dynamicVariables,
      createSession,
    ]
  );

  // Combined button click handler (orchestrates all hooks)
  const handleButtonClick = useCallback(async () => {
    if (startingRef.current) return;

    // Clear any stale error from a previous failed attempt before retrying.
    useWidgetStore.getState().clearError();

    if (!isValid) {
      storeUserError({
        title: 'Konfigurationsfehler',
        body: errors.join(', ') || 'Ungültige Widget-Konfiguration.',
        severity: 'error',
      });
      return;
    }
    if (!hasQuota || !quotaCheckComplete) {
      storeUserError(translateRuntimeError('quota'));
      return;
    }

    if (!conversationId) {
      startingRef.current = true;
      try {
        // Pre-flight voice-capability check (skip for pure chat mode — no mic needed)
        if (processedConfig.mode !== 'chat') {
          const compat = await checkVoiceCompatibility();
          if (!compat.ok) {
            storeUserError(compatibilityError(compat.reason));
            return;
          }
        }

        await startWidgetConversation(
          processedConfig.mode === 'chat',
          processedConfig.mode === 'chat' ? 'text' : 'voice'
        );
      } catch (err) {
        console.error('[Widget] Start conversation failed:', err);
        storeUserError(translateRuntimeError(err));
        useWidgetStore.getState().setButtonState('idle');
      } finally {
        startingRef.current = false;
      }
    } else if (
      processedConfig.mode === 'combined' &&
      activeSessionModeRef.current === 'text'
    ) {
      startingRef.current = true;
      try {
        const compat = await checkVoiceCompatibility();
        if (!compat.ok) {
          storeUserError(compatibilityError(compat.reason));
          return;
        }

        const handoffMessages = useWidgetStore.getState().messages;

        await switchTextSessionToVoice({
          stopTextSession: () => stopConversation({ preserveConversation: true }),
          endUsageSession: endSession,
          startVoiceSession: () => startWidgetConversation(false, 'voice'),
          conversation,
          messages: handoffMessages,
        });
      } catch (err) {
        console.error('[Widget] Switch to voice failed:', err);
        activeSessionModeRef.current = null;
        storeUserError(translateRuntimeError(err));
        useWidgetStore.getState().setButtonState('idle');
      } finally {
        startingRef.current = false;
      }
    } else {
      // Stop conversation
      await stopConversation();
      await endSession();
      activeSessionModeRef.current = null;
    }
  }, [
    isValid,
    errors,
    hasQuota,
    quotaCheckComplete,
    conversationId,
    processedConfig,
    startWidgetConversation,
    stopConversation,
    endSession,
    conversation,
  ]);

  const { sendMessage } = useMessageHandling(
    conversation,
    conversationId,
    hasQuota,
    isWidgetActive,
    widgetId,
    async () => {
      // Chat-only path: no mic required, skip voice compatibility check.
      try {
        const newConversationId = await startWidgetConversation(true, 'text');
        return newConversationId;
      } catch (err) {
        console.error('[Widget] Chat start failed:', err);
        storeUserError(translateRuntimeError(err));
        useWidgetStore.getState().setButtonState('idle');
        return undefined;
      }
    },
    markUserActivity
  );

  const connect = useCallback(async () => {
    // This can be used to pre-warm the connection if needed
    // For now, connection is established on first interaction
  }, []);

  const disconnect = useCallback(async () => {
    await stopConversation();
    activeSessionModeRef.current = null;
  }, [stopConversation]);

  const destroy = useCallback(async () => {
    await stopConversation();
    await endSession();
    activeSessionModeRef.current = null;
  }, [stopConversation, endSession]);

  return {
    buttonState,
    connectionStatus,
    isConnected,
    isRecording: useWidgetStore(selectIsRecording),
    isAgentSpeaking: useWidgetStore(selectIsAgentSpeaking), // из store, не из SDK
    isAgentThinking,
    error,
    config: processedConfig,
    shellDecided,
    messages,
    transcript,
    conversationItems,
    hasQuota,
    quotaCheckComplete,
    isWidgetActive,
    isWidgetDisabled,
    widgetId,
    widgetName,
    welcomeMessage,
    handleButtonClick,
    sendMessage,
    markUserActivity,
    stopConversation,
    connect,
    disconnect,
    destroy,
    getState: useCallback(() => ({
      isConnected,
      isRecording: useWidgetStore.getState().isRecording,
      buttonState: useWidgetStore.getState().buttonState,
      error: useWidgetStore.getState().error,
    }), [isConnected]),
    addTestSuggestion,
  };
};
